// Each distinct good filed under this item's id, with its own market.
//
// Migration 005_merge_ocr_variant_items.sql is the fact this exists to put
// back: an enchanted or augmented listing is stored under the *plain* item's
// id, with the difference in three columns. That keeps the item one item and
// keeps an augmented weapon findable by asking for the weapon, and it leaves
// one page's worth of offers describing several goods that trade at prices an
// order of magnitude apart. The page's headline figures and its price chart are
// the plain item's — deliberately, see server/lib/queries.py's `item_history` —
// so without this panel a weapon whose whole market is +6s has a page that says
// nothing about the market it actually has.
//
// Derived in the browser from rows /items/{id}/offers already returns. No
// endpoint: every field this needs is on an ItemObservation, and a second
// aggregate over the same rows is a second place for the same arithmetic to be
// wrong in.
//
// Outside the component for the reason offersSort.ts and itemQuote.ts both
// record: vitest here has no React renderer, so anything left inside a
// component cannot be tested, and a price attributed to the wrong good is the
// worst kind of quietly wrong.
import type { ItemObservation, Variation } from "../api/types";
import { variationParts } from "../format";
import { byText } from "./compare";
import { itemQuote, type Quote } from "./itemQuote";

export interface VariantGroup extends Variation {
  /** This good's identity among the item's variants, and what a list of these
   *  is keyed on. `label` cannot serve: variationParts flattens the triple to a
   *  line of words, so an element "Fire Focus" and an element "Fire" with the
   *  ability "Focus" spell the same label and are two different weapons. */
  key: string;
  /** What the store window called this one, minus the item's own name:
   *  "plain", "+6", "+6 Fire Focus".
   *
   *  Built from format.ts's `variationParts`, which is what components/
   *  Variation.tsx renders as pills and what queries.py's `_LISTED_NAME`
   *  spells the same way — so "+6 Fire" means the same thing in a lot's item
   *  list, beside a trader's nick and here. "plain" is this module's own word,
   *  because the others render nothing at all for a listing that added
   *  nothing, and a row in this table needs a name. */
  label: string;
  /** The best bid and the best ask for *this* good, by exactly the rule the
   *  page's headline figures use: itemQuote's, newest scan first. */
  buy: Quote | null;
  sell: Quote | null;
  /** How many bids and asks of this variant the page has, across every scan.
   *
   *  Depth, and the column that stops the panel reading as a price list. Two
   *  sightings of a +6 is one trader who has stood there twice; twenty-three of
   *  the plain item is a market. It counts the same rows the two quotes are
   *  drawn from — priced bids and asks only — so a reader comparing the number
   *  against the tables below is comparing like with like. */
  offers: number;
}

/** The variant key, as a string, so a Map can group on all three columns at
 *  once.
 *
 *  JSON rather than a joined string, and not for tidiness: an ability is real
 *  prose with spaces in it ("Critical Damage"), so any separator that can occur
 *  in the data collides. An element "Fire Focus" with no ability and an element
 *  "Fire" with the ability "Focus" are two different weapons, and joining on a
 *  space hands them one key. JSON has no such seam, and it keeps null apart
 *  from "" for free.
 *
 *  An enchant of 0 and a null enchant are one key, matching `_PLAIN_ITEM`'s
 *  `COALESCE(o.enchant, 0) = 0`: the client writes 0 where the store window
 *  showed no "+N", and older rows predate the column. Split, a source that
 *  reports 0 would give every plain item two rows in this panel that mean the
 *  same thing. */
function keyOf(row: Variation): string {
  return JSON.stringify([row.enchant || 0, row.element, row.ability]);
}

/** Every distinct good this item's offers describe, plain first.
 *
 *  Built only from priced bids and asks — the rows itemQuote itself reads. A
 *  craft fee is a crafter's charge for making one and a package row is one item
 *  of a lot with a single price between its members, so neither is a price for
 *  a variant of this item, and a group made of nothing but those two would be a
 *  row in this table with both price columns empty.
 *
 *  Ordered plain, then by enchant, then by element, then by ability. Numerically
 *  on the level, which is the whole reason the sort is not on the label: "+10"
 *  sorts before "+6" as text, and a table of enchant levels out of order reads
 *  as a bug in the first two seconds. */
export function variantGroups(offers: ItemObservation[]): VariantGroup[] {
  const groups = new Map<string, ItemObservation[]>();
  for (const offer of offers) {
    if (offer.mode !== "buy" && offer.mode !== "sell") continue;
    if (offer.price === null) continue;
    const key = keyOf(offer);
    const rows = groups.get(key);
    if (rows === undefined) groups.set(key, [offer]);
    else rows.push(offer);
  }

  const out: VariantGroup[] = [];
  for (const [key, rows] of groups) {
    // itemQuote, not a second implementation of it. The rule it applies —
    // newest *scan*, then the best price within that scan — is stated once in
    // its own comment, and a copy of it here is a copy that drifts: this panel
    // sitting beside the headline figures is the one place a disagreement would
    // be visible and the one place nobody would believe it.
    const quotes = itemQuote(rows);
    const { enchant, element, ability } = rows[0];
    const parts = variationParts(rows[0]);
    out.push({
      key,
      // Read off the first row rather than parsed back out of the key, so the
      // three fields a caller spreads into <Variation /> are the API's own
      // values — a null ability stays null instead of becoming "".
      enchant, element, ability,
      label: parts.length === 0 ? "plain" : parts.join(" "),
      buy: quotes.buy,
      sell: quotes.sell,
      offers: rows.length,
    });
  }

  return out.sort((a, b) =>
    (a.enchant || 0) - (b.enchant || 0)
    || byText(a.element ?? "", b.element ?? "")
    || byText(a.ability ?? "", b.ability ?? ""));
}

/** Whether these groups are worth a panel of their own on the item page.
 *
 *  The rule the item page renders on, here rather than inside the component so
 *  that it can be tested — a panel that fails to appear is not something a
 *  reader reports, because they have no way to know it was missing.
 *
 *  Nothing to show for an item nobody has bid on or asked for, and nothing for
 *  the ordinary item whose one group is the plain one: that is every consumable
 *  and every material, all three variation columns null on every row, and a
 *  panel headed "Variants" over a single line reading "plain" is a heading over
 *  a thing the page has already said twice above it.
 *
 *  NOT simply "more than one group", which is the tempting form and is wrong in
 *  the one case this panel exists for. A weapon nobody has ever listed plain has
 *  exactly ONE group, and its page is the one page on the site with no price
 *  chart (`item_history` is the plain item, and there is none) and a headline
 *  figure drawn from whichever variant happened to win. Hiding the panel there
 *  hides the only prices that page has. */
export function variantsWorthShowing(groups: VariantGroup[]): boolean {
  if (groups.length > 1) return true;
  return groups.length === 1 && variationParts(groups[0]).length > 0;
}
